import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FiGrid, FiBookmark, FiEdit2, FiCamera, FiLink } from 'react-icons/fi';
import { usersAPI, postsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function Profile() {
  const { id }                     = useParams();
  const { user: me, updateUser }   = useAuth();
  const [profile, setProfile]      = useState(null);
  const [posts, setPosts]          = useState([]);
  const [tab, setTab]              = useState('posts');
  const [loading, setLoading]      = useState(true);
  const [editing, setEditing]      = useState(false);
  const [editForm, setEditForm]    = useState({});
  const [saving, setSaving]        = useState(false);
  const [following, setFollowing]  = useState(false);
  const avatarRef                  = useRef();

  const userId = +id || me?.id;
  const isMe   = userId === me?.id;

  useEffect(() => {
    setLoading(true);
    Promise.all([usersAPI.getProfile(userId), postsAPI.getUserPosts(userId)])
      .then(([pr, po]) => {
        setProfile(pr.data.user);
        setFollowing(pr.data.user.is_following);
        setPosts(po.data.posts || []);
      })
      .catch(() => toast.error('Failed to load profile.'))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleFollow = async () => {
    const was = following;
    setFollowing(!was);
    setProfile(p => ({ ...p, followers_count: was ? p.followers_count - 1 : p.followers_count + 1 }));
    try { await usersAPI.toggleFollow(userId); }
    catch { setFollowing(was); setProfile(p => ({ ...p, followers_count: was ? p.followers_count + 1 : p.followers_count - 1 })); }
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await usersAPI.updateProfile(editForm);
      setProfile(p => ({ ...p, ...editForm }));
      updateUser(editForm);
      setEditing(false);
      toast.success('Profile updated!');
    } catch { toast.error('Failed to save.'); }
    finally { setSaving(false); }
  };

  const handleAvatarChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('avatar', file);
    const tid = toast.loading('Updating avatar…');
    try {
      const res = await usersAPI.uploadAvatar(fd);
      setProfile(p => ({ ...p, avatar: res.data.avatar }));
      updateUser({ avatar: res.data.avatar });
      toast.success('Avatar updated!', { id: tid });
    } catch { toast.error('Failed.', { id: tid }); }
    e.target.value = '';
  };

  if (loading) return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-5)', marginBottom: 'var(--sp-5)' }}>
        <div className="skeleton" style={{ width: 'clamp(68px,15vw,106px)', height: 'clamp(68px,15vw,106px)', borderRadius: '50%', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div className="skeleton" style={{ width: '45%', height: 14, marginBottom: 12 }} />
          <div className="skeleton" style={{ width: '65%', height: 10 }} />
        </div>
      </div>
    </div>
  );

  if (!profile) return <div style={{ padding: 40, textAlign: 'center' }}>User not found.</div>;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="profile-header">
        {/* Avatar */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div className="profile-avatar-wrap">
            <div className="profile-avatar-inner">
              {profile.avatar
                ? <img src={profile.avatar} alt="" />
                : <div className="profile-avatar-ph">{profile.full_name?.[0]}</div>
              }
            </div>
          </div>
          {isMe && (
            <>
              <button className="avatar-edit-btn" onClick={() => avatarRef.current?.click()}>
                <FiCamera size={12} />
              </button>
              <input ref={avatarRef} type="file" accept="image/*" hidden onChange={handleAvatarChange} />
            </>
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap', marginBottom: 'var(--sp-3)' }}>
            <h2 style={{ fontSize: 'var(--fs-xl)' }}>{profile.username}</h2>
            {profile.is_verified && <span style={{ color: 'var(--primary)', fontSize: 'var(--fs-lg)' }}>✓</span>}
            {isMe ? (
              <button className="btn btn-outline btn-sm" onClick={() => { setEditForm({ full_name: profile.full_name, bio: profile.bio || '', website: profile.website || '' }); setEditing(true); }}>
                <FiEdit2 size={13} /> Edit Profile
              </button>
            ) : (
              <button className={'btn btn-sm ' + (following ? 'btn-outline' : 'btn-primary')} onClick={handleFollow}>
                {following ? 'Following' : 'Follow'}
              </button>
            )}
          </div>

          <div className="profile-stats">
            <div className="stat"><strong>{profile.posts_count}</strong>posts</div>
            <div className="stat"><strong>{profile.followers_count}</strong>followers</div>
            <div className="stat"><strong>{profile.following_count}</strong>following</div>
          </div>

          <div style={{ marginTop: 'var(--sp-3)' }}>
            <p className="font-bold" style={{ fontSize: 'var(--fs-sm)' }}>{profile.full_name}</p>
            {profile.bio && <p style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.6, marginTop: 4, whiteSpace: 'pre-wrap' }}>{profile.bio}</p>}
            {profile.website && (
              <a href={profile.website} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-sm)', color: 'var(--primary)', marginTop: 4 }}>
                <FiLink size={13} /> {profile.website.replace(/^https?:\/\//, '')}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(false)}>
          <div className="modal-box card fade-in" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Edit Profile</h3><button className="btn btn-icon btn-ghost" onClick={() => setEditing(false)}>✕</button></div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
              <div className="input-group">
                <label>Full Name</label>
                <input className="input" value={editForm.full_name || ''} onChange={e => setEditForm(p => ({ ...p, full_name: e.target.value }))} />
              </div>
              <div className="input-group">
                <label>Bio</label>
                <textarea className="input" rows={3} style={{ resize: 'none' }} value={editForm.bio || ''} onChange={e => setEditForm(p => ({ ...p, bio: e.target.value }))} maxLength={150} />
              </div>
              <div className="input-group">
                <label>Website</label>
                <input className="input" value={editForm.website || ''} onChange={e => setEditForm(p => ({ ...p, website: e.target.value }))} placeholder="https://…" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleEditSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="profile-tabs">
        <button className={'profile-tab' + (tab === 'posts' ? ' active' : '')} onClick={() => setTab('posts')}>
          <FiGrid size={13} /> Posts
        </button>
        {isMe && (
          <button className={'profile-tab' + (tab === 'saved' ? ' active' : '')} onClick={() => setTab('saved')}>
            <FiBookmark size={13} /> Saved
          </button>
        )}
      </div>

      {/* Grid */}
      <div className="posts-grid">
        {posts.length === 0
          ? <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 'var(--sp-6)', color: 'var(--text-3)', fontSize: 'var(--fs-md)' }}>No posts yet.</div>
          : posts.map(p => (
              <div key={p.id} className="grid-post">
                {p.media_type === 'video'
                  ? <video src={p.media_url} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <img src={p.media_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                }
                <div className="grid-post-overlay">
                  <span>❤️ {p.likes_count}</span>
                  <span>💬 {p.comments_count}</span>
                </div>
              </div>
            ))
        }
      </div>
    </div>
  );
}
